/* tslint:disable */
/* eslint-disable */
import { Injectable } from '@angular/core';
import { HttpClient, HttpResponse, HttpContext } from '@angular/common/http';
import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { LeadsResponsePage } from '../models/leads-response-page';


/**
 * Sales leads — people who started the public invoice-financing wizard
 * without completing it (completing the wizard deletes the lead).
 */
@Injectable({
  providedIn: 'root',
})
export class LeadsService extends BaseService {
  constructor(
    config: ApiConfiguration,
    http: HttpClient
  ) {
    super(config, http);
  }

  /**
   * Path part for operation listLeads
   */
  static readonly ListLeadsPath = '/leads';

  /**
   * List leads.
   *
   * Returns a paginated list of leads. The order is a stable cursor-walk order only; consumers sort client-side (e.g. by last activity).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listLeads()` instead.
   *
   * This method doesn't expect any request body.
   */
  listLeads$Response(params?: {

    /**
     * Limit amount of returned results
     */
    limit?: number;

    /**
     * Cursor for the next page of results
     */
    after?: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<LeadsResponsePage>> {

    const rb = new RequestBuilder(this.rootUrl, LeadsService.ListLeadsPath, 'get');
    if (params) {
      rb.query('limit', params.limit, {});
      rb.query('after', params.after, {});
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<LeadsResponsePage>;
      })
    );
  }

  /**
   * List leads.
   *
   * Returns a paginated list of leads. The order is a stable cursor-walk order only; consumers sort client-side (e.g. by last activity).
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `listLeads$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listLeads(params?: {

    /**
     * Limit amount of returned results
     */
    limit?: number;

    /**
     * Cursor for the next page of results
     */
    after?: string;
    context?: HttpContext
  }
): Observable<LeadsResponsePage> {

    return this.listLeads$Response(params).pipe(
      map((r: StrictHttpResponse<LeadsResponsePage>) => r.body as LeadsResponsePage)
    );
  }

  /**
   * Path part for operation deleteLead
   */
  static readonly DeleteLeadPath = '/leads/{id}';

  /**
   * Delete a lead.
   *
   * Removes a lead after it has been handled (e.g. contacted). Low-stakes: the lead resurfaces if the person re-submits the wizard.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteLead()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteLead$Response(params: {
    id: string;
    context?: HttpContext
  }
): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, LeadsService.DeleteLeadPath, 'delete');
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }

  /**
   * Delete a lead.
   *
   * Removes a lead after it has been handled (e.g. contacted). Low-stakes: the lead resurfaces if the person re-submits the wizard.
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `deleteLead$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteLead(params: {
    id: string;
    context?: HttpContext
  }
): Observable<void> {

    return this.deleteLead$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

}
